import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Dimensions,
  useWindowDimensions,
  TouchableOpacity,
  TextInput,
  KeyboardAvoidingView,
  FlatList,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { useRTLStyles } from '../hooks/useRTLStyles';
import * as Contacts from 'expo-contacts';
import * as FileSystem from 'expo-file-system';
import { colors, spacing, typography } from '../theme';
import { RichTextEditor } from '../components/design-system/RichTextEditor';
import { Button } from '../components/design-system/Button';
import { Input } from '../components/design-system/Input';
import { ScreenLayout } from '../components/ScreenLayout';
import { Header } from '../components/Header';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService } from '../services/api';
import { parseInternationalPhoneNumber } from '../utils/phoneNumber';
import {
  ContactData,
  BulkMessageData,
  RecipientChip,
} from '../components/WhatsAppMessageForm/types/messageForm.types';
import { useContactSelection } from '../components/WhatsAppMessageForm/hooks/useContactSelection';
import { useMessageSending } from '../components/WhatsAppMessageForm/hooks/useMessageSending';
import { useCustomTemplates } from '../components/WhatsAppMessageForm/hooks/useCustomTemplates';
import { useTemplateProcessing } from '../components/WhatsAppMessageForm/hooks/useTemplateProcessing';
import { TemplateList } from '../components/WhatsAppMessageForm/components/TemplateList';
import { RecipientChipsInput } from '../components/WhatsAppMessageForm/components/RecipientChipsInput';
import { WhatsAppMessagePreview } from '../components/WhatsAppMessageForm/components/WhatsAppMessagePreview';
import { WhatsAppMessagePreviewEditable } from '../components/WhatsAppMessageForm/components/WhatsAppMessagePreviewEditable';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { defaultPhoneCountry } from '../utils/businessDefaults';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isNativeMobile = Platform.OS !== 'web';

type WizardStep = 1 | 2 | 3 | 4;

export const SendWhatsAppMessageScreen: React.FC = () => {
  const rtl = useRTLStyles();
  const styles = useThemedStyles(getStyles);
  const { width: winWidth } = useWindowDimensions();
  const isMobile = isNativeMobile || winWidth < 768;
  const { currentBusiness, currentSubscription } = useAppStore();
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();

  // ─── Wizard step ──────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);

  // ─── Recipients ───────────────────────────────────────────────────────────
  const [recipients, setRecipients] = useState<RecipientChip[]>([]);

  // ─── Message ──────────────────────────────────────────────────────────────
  const [message, setMessage] = useState('');
  const [taskName, setTaskName] = useState('');

  // ─── Template ─────────────────────────────────────────────────────────────
  const [messageSource, setMessageSource] = useState<'custom-message' | 'custom-template'>('custom-template');
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateParameters, setTemplateParameters] = useState<Record<number, string>>({});
  const [headerAsset, setHeaderAsset] = useState<any | undefined>(undefined);
  const [parameterErrors, setParameterErrors] = useState<string[]>([]);

  // ─── Schedule ─────────────────────────────────────────────────────────────
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date>(new Date());
  const [scheduleHour, setScheduleHour] = useState<number>(new Date().getHours());
  const [scheduleMinute, setScheduleMinute] = useState<number>(new Date().getMinutes());
  const [isAM, setIsAM] = useState<boolean>(new Date().getHours() < 12);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);

  // ─── Limits ───────────────────────────────────────────────────────────────
  const [messageLimits, setMessageLimits] = useState<{
    canSend: boolean;
    message: string;
    current: number;
    max: number;
  } | null>(null);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ─── Device contact picker ────────────────────────────────────────────────
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [deviceContactsList, setDeviceContactsList] = useState<Contacts.Contact[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [contactPickerLoading, setContactPickerLoading] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');

  // ─── Hooks ────────────────────────────────────────────────────────────────
  const { contacts: importedContacts, importFromDevice, importFromFile, clearContacts, isLoading: isImporting } =
    useContactSelection();
  const { sendBulkMessages, isSending } = useMessageSending();
  const { templates, defaultTemplate, isLoading: isLoadingTemplates, hasTemplates } = useCustomTemplates();
  const { processTemplate } = useTemplateProcessing();

  // ─── 12h helpers ─────────────────────────────────────────────────────────
  const get12HourFormat = useCallback((hour24: number) => {
    if (hour24 === 0) return 12;
    if (hour24 <= 12) return hour24;
    return hour24 - 12;
  }, []);

  const convertTo24Hour = useCallback((hour12: number, am: boolean) => {
    if (hour12 === 12) return am ? 0 : 12;
    return am ? hour12 : hour12 + 12;
  }, []);

  // ─── Computed ─────────────────────────────────────────────────────────────
  const recipientCount = recipients.length;

  const isLimitReached = useMemo(() => messageLimits ? !messageLimits.canSend : false, [messageLimits]);

  const defaultTaskName = useMemo(
    () => `Send WhatsApp Messages - ${recipientCount} contact${recipientCount === 1 ? '' : 's'}`,
    [recipientCount]
  );

  // Positions are global/unique across a template's header+body+footer placeholders
  // (Meta's own numbering convention — header {{1}} and body {{2}} are two different
  // slots, never both "{{1}}"), so a single position-keyed `templateParameters` map is
  // safe as long as the template itself doesn't reuse a number across sections — which
  // TemplateManagementScreen's create/edit form now validates against at save time.
  const derivedTemplateParameters = useMemo(() => {
    if (!selectedTemplate) return [] as any[];
    if (selectedTemplate.parameters && selectedTemplate.parameters.length > 0) {
      return selectedTemplate.parameters.filter((p: any) => p.section === 'header' || p.section === 'body' || p.section === 'footer');
    }
    const positionsBySection = new Map<number, Set<'header' | 'body' | 'footer'>>();
    const extractPositions = (text: string | null | undefined, section: 'header' | 'body' | 'footer') => {
      if (!text) return;
      const regex = /\{\{(\d+)\}\}/g;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        const pos = Number(match[1]);
        if (Number.isNaN(pos)) continue;
        if (!positionsBySection.has(pos)) positionsBySection.set(pos, new Set());
        positionsBySection.get(pos)!.add(section);
      }
    };
    extractPositions(selectedTemplate.templateHeader, 'header');
    extractPositions(selectedTemplate.templateBody, 'body');
    extractPositions(selectedTemplate.templateFooter, 'footer');
    return Array.from(positionsBySection.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([position, sections]) => {
        const section = sections.has('header') ? 'header' : sections.has('body') ? 'body' : 'footer';
        return { position, section, name: `Parameter ${position}`, isRequired: true };
      });
  }, [selectedTemplate]);

  const missingParamCount = useMemo(() => {
    return derivedTemplateParameters.filter(
      (p: any) => p.isRequired && !templateParameters[p.position]?.trim()
    ).length;
  }, [derivedTemplateParameters, templateParameters]);

  const canProceedToStep2 = recipientCount > 0;

  const canProceedToStep3 = useMemo(() => {
    if (!selectedTemplate) return false;
    if (missingParamCount > 0) return false;
    const assetType = selectedTemplate.headerAsset?.type;
    if (assetType) {
      if (assetType === 'location') return !!(headerAsset?.mediaId);
      return !!(headerAsset?.url);
    }
    return true;
  }, [selectedTemplate, missingParamCount, headerAsset]);

  const filteredDeviceContacts = useMemo(() => {
    if (!contactSearchQuery.trim()) return deviceContactsList;
    const q = contactSearchQuery.trim().toLowerCase();
    return deviceContactsList.filter(
      c =>
        (c.name || '').toLowerCase().includes(q) ||
        c.phoneNumbers?.some(p => (p.number || '').replace(/\D/g, '').includes(q.replace(/\D/g, '')))
    );
  }, [deviceContactsList, contactSearchQuery]);

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setActiveTab('home');
    loadMessageLimits();
  }, []);

  // Seed from routeParams (contacts screen bulk WhatsApp)
  useEffect(() => {
    const bulk = routeParams?.bulkContacts;
    if (!Array.isArray(bulk) || bulk.length === 0) return;
    const chips: RecipientChip[] = bulk
      .map((c: { name?: string; phoneNumber?: string; contactId?: string }, i: number) => {
        const raw = (c.phoneNumber ?? '').trim();
        if (!raw) return null;
        const withPlus = raw.startsWith('+') ? raw : `+${raw}`;
        const parsed = parseInternationalPhoneNumber(withPlus);
        return {
          id: `bulk-${Date.now()}-${i}`,
          displayName: (c.name ?? '').trim() || withPlus,
          formattedPhone: withPlus,
          dialCode: parsed?.countryCode ?? '+20',
          countryCode: (parsed?.country ?? defaultPhoneCountry()) as string,
          isExisting: !!c.contactId,
          isValid: true,
          // Threaded through to the send payload below so the backend can re-resolve
          // the REAL number server-side instead of trusting formattedPhone outright —
          // formattedPhone here was already resolved via getContactSendTargets
          // (ContactsListScreen.handleBulkWhatsAppPress), not read off masked data, but
          // this keeps the server-side defense-in-depth check consistent regardless.
          existingContactId: c.contactId,
        } as RecipientChip;
      })
      .filter((c): c is RecipientChip => c !== null && c.formattedPhone.length > 3);
    if (chips.length > 0) setRecipients(chips);
  }, [routeParams?.bulkContacts]);

  // Merge contacts imported from file/device hook
  useEffect(() => {
    if (importedContacts.length === 0) return;
    const chips: RecipientChip[] = importedContacts.map((contact, i) => {
      const raw = (contact.phoneNumber ?? '').trim();
      const withPlus = raw.startsWith('+') ? raw : `+${raw}`;
      const parsed = parseInternationalPhoneNumber(withPlus);
      const formattedPhone = withPlus.startsWith('+') ? withPlus : `+${withPlus}`;
      return {
        id: `imported-${Date.now()}-${i}`,
        displayName: contact.name || formattedPhone,
        formattedPhone,
        dialCode: contact.dialCode || '+20',
        countryCode: (parsed?.country ?? contact.countryCode ?? defaultPhoneCountry()) as string,
        isExisting: !!contact.isExisting,
        isValid: true,
        existingContactId: contact.existingContactId,
      };
    });
    setRecipients(prev => {
      const existing = new Set(prev.map(r => r.formattedPhone));
      return [...prev, ...chips.filter(c => !existing.has(c.formattedPhone))];
    });
    clearContacts();
  }, [importedContacts, clearContacts]);

  // Auto-select default template on mount
  useEffect(() => {
    if (!selectedTemplate && defaultTemplate) {
      setSelectedTemplate(defaultTemplate);
      setTemplateParameters({});
      setHeaderAsset(defaultTemplate.headerAsset || undefined);
    }
  }, [defaultTemplate]);

  // Seed from cloneParams (task history clone/resend)
  useEffect(() => {
    const clone = routeParams?.cloneParams;
    if (!clone || templates.length === 0) return;
    if (clone.templateName) {
      const tpl = templates.find((t: any) => t.templateName === clone.templateName);
      if (tpl) {
        setSelectedTemplate(tpl);
        setHeaderAsset(clone.headerAsset || tpl.headerAsset || undefined);
        setMessageSource('custom-template');
        if (Array.isArray(clone.parameters) && clone.parameters.length > 0) {
          const paramMap: Record<number, string> = {};
          clone.parameters.forEach((v: string, i: number) => { paramMap[i + 1] = v; });
          setTemplateParameters(paramMap);
        }
      }
    } else if (clone.message) {
      setMessage(clone.message);
      setMessageSource('custom-message');
    }
  }, [routeParams?.cloneParams, templates]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const loadMessageLimits = useCallback(async () => {
    try {
      setIsLoadingLimits(true);
      const response = await apiService.checkWhatsAppLimits();
      if (response.statusCode === 1000 && response.data) {
        setMessageLimits(response.data);
      }
    } catch {
      setMessageLimits({ canSend: true, message: 'Unable to load limits', current: 0, max: 500 });
    } finally {
      setIsLoadingLimits(false);
    }
  }, []);

  const handleAddRecipient = useCallback((chip: RecipientChip) => {
    setRecipients(prev => [...prev, chip]);
    // Async contact existence check – updates the chip if found
    apiService
      .checkContactExists(chip.formattedPhone)
      .then(response => {
        if (response.statusCode === 1000 && response.data?.exists) {
          setRecipients(prev =>
            prev.map(r =>
              r.id === chip.id
                ? {
                    ...r,
                    isExisting: true,
                    displayName: response.data.contact.name || r.displayName,
                    existingContactId: response.data.contact._id,
                  }
                : r
            )
          );
        }
      })
      .catch(() => {});
  }, []);

  const handleRemoveRecipient = useCallback((id: string) => {
    setRecipients(prev => prev.filter(r => r.id !== id));
  }, []);

  const handleImportFromDevice = async () => {
    try {
      const { status } = await Contacts.requestPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Required', 'Please grant contacts permission in settings');
        return;
      }
      if (Platform.OS === 'web') {
        await importFromDevice();
        return;
      }
      setContactPickerLoading(true);
      setShowContactPicker(true);
      setSelectedContactIds(new Set());
      setContactSearchQuery('');
      try {
        const { data } = await Contacts.getContactsAsync({
          fields: [Contacts.Fields.Name, Contacts.Fields.PhoneNumbers],
          sort: Contacts.SortTypes.FirstName,
        });
        const withPhone = (data || []).filter(
          c => c.phoneNumbers && c.phoneNumbers.length > 0 && (c.phoneNumbers[0].number || '').trim().length >= 8
        );
        setDeviceContactsList(withPhone);
        if (withPhone.length === 0) {
          Alert.alert('No Contacts', 'No contacts with valid phone numbers found.');
          setShowContactPicker(false);
        }
      } catch (err: any) {
        Alert.alert('Error', err.message || 'Failed to load contacts');
        setShowContactPicker(false);
      } finally {
        setContactPickerLoading(false);
      }
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to import contacts');
    }
  };

  const toggleContactSelection = (id: string | undefined, index: number) => {
    const key = id ?? `contact-${index}`;
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllContacts = () => {
    const filtered = filteredDeviceContacts;
    if (selectedContactIds.size === filtered.length) {
      setSelectedContactIds(new Set());
    } else {
      setSelectedContactIds(new Set(filtered.map((c, i) => c.id ?? `contact-${i}`)));
    }
  };

  const addSelectedContactsToForm = () => {
    const selected = filteredDeviceContacts.filter((c, i) => selectedContactIds.has(c.id ?? `contact-${i}`));
    if (selected.length === 0) {
      Alert.alert('No Selection', 'Please select at least one contact.');
      return;
    }
    const chips: RecipientChip[] = selected.map((contact, i) => {
      const raw = (contact.phoneNumbers?.[0]?.number || '').trim();
      const withPlus = raw.startsWith('+') ? raw : `+${raw.replace(/\D/g, '')}`;
      const parsed = parseInternationalPhoneNumber(withPlus);
      return {
        id: `picker-${Date.now()}-${i}`,
        displayName: (contact.name || '').trim() || withPlus,
        formattedPhone: withPlus,
        dialCode: parsed?.countryCode ?? '+20',
        countryCode: (parsed?.country ?? defaultPhoneCountry()) as string,
        isExisting: false,
        isValid: true,
      };
    });
    setRecipients(prev => {
      const existing = new Set(prev.map(r => r.formattedPhone));
      return [...prev, ...chips.filter(c => !existing.has(c.formattedPhone))];
    });
    setShowContactPicker(false);
    setSelectedContactIds(new Set());
    setDeviceContactsList([]);
  };

  const handleImportFromFile = async () => {
    try {
      await importFromFile();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to import file');
    }
  };

  const handleDownloadTemplate = async () => {
    const templateContent =
      'name,phone_number,email,tags\n' +
      'Ahmed Ali,+201234567890,ahmed@example.com,customer|vip\n' +
      'Sara Mahmoud,+966512345678,sara@example.com,lead|saudi\n';
    if (Platform.OS === 'web') {
      const blob = new Blob([templateContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sendifier_whatsapp_template.csv';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showNotification('success', 'Template Downloaded', 'Check your downloads folder.');
    } else {
      try {
        const fileUri = `${FileSystem.documentDirectory}sendifier_whatsapp_template.csv`;
        await FileSystem.writeAsStringAsync(fileUri, templateContent, { encoding: FileSystem.EncodingType.UTF8 });
        // Try to share/open the file
        const { Share } = await import('react-native');
        await Share.share({ url: fileUri, title: 'sendifier_whatsapp_template.csv' });
      } catch {
        Alert.alert(
          'CSV Template Format',
          'Columns: name, phone_number, email, tags\nExample:\nAhmed Ali,+201234567890,ahmed@example.com,customer|vip',
          [{ text: 'OK' }]
        );
      }
    }
  };

  const handleSend = async () => {
    if (messageSource === 'custom-message') {
      if (!message.trim() || message.trim().length < 15) {
        Alert.alert('Invalid Message', 'Please enter a message with at least 15 characters.');
        return;
      }
    } else if (messageSource === 'custom-template') {
      if (!selectedTemplate) {
        Alert.alert('No Template', 'Please select a template.');
        return;
      }
      if (selectedTemplate.headerAsset?.type) {
        const expectedType = selectedTemplate.headerAsset.type;
        const hasAsset = headerAsset && headerAsset.type === expectedType;
        if (expectedType === 'location' && (!hasAsset || !headerAsset?.mediaId)) {
          Alert.alert('Missing Location', 'Please select a location for the template header.');
          return;
        }
        if (expectedType !== 'location' && (!hasAsset || !headerAsset?.url)) {
          Alert.alert('Missing Header Asset', `Please upload a ${expectedType} for the template header.`);
          return;
        }
      }
      if (derivedTemplateParameters.length > 0) {
        const missing = derivedTemplateParameters.filter(
          (p: any) => p.isRequired && !templateParameters[p.position]?.trim()
        );
        if (missing.length > 0) {
          Alert.alert(
            'Missing Parameters',
            `Please fill in all required parameters: ${missing.map((p: any) => p.name || `Parameter ${p.position}`).join(', ')}`
          );
          return;
        }
      }
    }

    if (recipients.length === 0) {
      Alert.alert('No Recipients', 'Please add at least one phone number.');
      return;
    }

    if (messageLimits && !messageLimits.canSend) {
      Alert.alert('Limit Reached', messageLimits.message || 'You have reached your message limit.');
      return;
    }

    // The backend silently truncates a send to whatever quota remains today rather
    // than rejecting it outright — that's the right behavior for the send itself,
    // but the user should know BEFORE sending that only part of their selected
    // list will actually be messaged today, not discover it after the fact from
    // the task's result. Only warn (not block) since sending the partial batch is
    // still a valid choice.
    if (messageLimits && !isScheduleEnabled) {
      const remainingQuota = messageLimits.max - messageLimits.current;
      if (recipients.length > remainingQuota && remainingQuota > 0) {
        const proceed = await new Promise<boolean>((resolve) => {
          Alert.alert(
            'Daily Limit Will Cut This Short',
            `You selected ${recipients.length} recipients, but only ${remainingQuota} message${remainingQuota === 1 ? '' : 's'} remain in today's limit. The other ${recipients.length - remainingQuota} will NOT be sent today. Send to the first ${remainingQuota} now?`,
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: `Send to ${remainingQuota}`, onPress: () => resolve(true) },
            ]
          );
        });
        if (!proceed) return;
      }
    }

    const contactsToSend: ContactData[] = recipients.map(chip => ({
      name: chip.displayName,
      phoneNumber: chip.formattedPhone,
      countryCode: chip.countryCode,
      dialCode: chip.dialCode,
      isValid: chip.isValid,
      isExisting: chip.isExisting,
      existingContactId: chip.existingContactId,
    }));

    try {
      setIsLoading(true);

      let schedule: { enabled: boolean; scheduledAt?: string } | undefined;
      if (isScheduleEnabled) {
        const scheduledDateTime = new Date(scheduleDate);
        scheduledDateTime.setHours(scheduleHour, scheduleMinute, 0, 0);
        if (scheduledDateTime <= new Date()) {
          Alert.alert('Invalid Schedule', 'Scheduled time must be in the future.');
          return;
        }
        schedule = { enabled: true, scheduledAt: scheduledDateTime.toISOString() };
      }

      let finalMessage = '';
      if (messageSource === 'custom-template' && selectedTemplate) {
        if (selectedTemplate.hasParameters && selectedTemplate.parameters?.length > 0) {
          const result = await processTemplate(selectedTemplate._id, templateParameters) as any;
          if (!result) {
            Alert.alert('Template Error', 'Failed to process template');
            return;
          }
          if (result.success && result.processedMessage) {
            finalMessage = result.processedMessage;
          } else if (result.processed?.body) {
            finalMessage = result.processed.body;
          } else {
            Alert.alert('Template Error', result.error || 'Failed to process template');
            return;
          }
        } else {
          finalMessage = selectedTemplate.templateBody;
        }
      } else {
        finalMessage = message.trim();
      }

      // array[position - 1] = value, matching how the backend re-derives each
      // section's value by global position (see buildTemplateComponents).
      const templateParamArray =
        messageSource === 'custom-template' && derivedTemplateParameters.length > 0
          ? (() => {
              const maxPosition = Math.max(...derivedTemplateParameters.map((p: any) => p.position));
              const ordered = new Array(maxPosition).fill('');
              derivedTemplateParameters.forEach((p: any) => {
                ordered[p.position - 1] = templateParameters[p.position] ?? '';
              });
              return ordered;
            })()
          : undefined;

      const messageData: BulkMessageData = {
        contacts: contactsToSend,
        message: finalMessage,
        taskName: taskName.trim() || defaultTaskName,
        templateName: messageSource === 'custom-template' ? selectedTemplate?.templateName : undefined,
        templateLanguage: messageSource === 'custom-template' ? selectedTemplate?.language : undefined,
        parameters: templateParamArray,
        headerAsset: messageSource === 'custom-template' ? headerAsset : undefined,
        schedule,
      };

      const results = await sendBulkMessages(messageData);
      const hasErrors = results.some((r: any) => !r.success && r.error);
      const successful = results.filter((r: any) => r.success).length;
      const pending = results.filter((r: any) => !r.success && !r.error).length;
      const failed = results.filter((r: any) => !r.success && r.error).length;

      if (hasErrors) {
        if (successful > 0) {
          showNotification('warning', 'Partial Success', `Task created for ${successful + pending} contact(s), ${failed} failed.`);
        } else {
          showNotification('error', 'Task Creation Failed', `Failed to create task for ${failed} contact(s).`);
        }
      } else {
        // Show success step
        setCurrentStep(4);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to send messages');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setRecipients([]);
    setMessage('');
    setTaskName('');
    setMessageSource('custom-message');
    setSelectedTemplate(null);
    setTemplateParameters({});
    setHeaderAsset(undefined);
    setIsScheduleEnabled(false);
    const now = new Date();
    setScheduleDate(now);
    setScheduleHour(now.getHours());
    setScheduleMinute(now.getMinutes());
    setIsAM(now.getHours() < 12);
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderCalendarDays = useCallback(() => {
    const year = scheduleDate.getFullYear();
    const month = scheduleDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const today = new Date();
    const selectedDateOnly = new Date(scheduleDate);
    selectedDateOnly.setHours(0, 0, 0, 0);
    const days = [];
    const now = today.getTime();
    const selected = selectedDateOnly.getTime();
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<View key={`empty-${i}`} style={styles.calendarDayCell} />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      currentDate.setHours(0, 0, 0, 0);
      const currentDateOnly = currentDate.getTime();
      const isPast = currentDateOnly < now - 86400000;
      const isSelected = currentDateOnly === selected;
      days.push(
        <TouchableOpacity
          key={`day-${day}`}
          style={[styles.calendarDayCell, isSelected && styles.calendarDaySelected, isPast && styles.calendarDayPast]}
          onPress={() => {
            if (!isPast) {
              const newDate = new Date(year, month, day);
              newDate.setHours(scheduleHour, scheduleMinute);
              setScheduleDate(newDate);
            }
          }}
          disabled={isPast}
        >
          <Text style={[styles.calendarDayText, isSelected && styles.calendarDayTextSelected, isPast && styles.calendarDayTextPast]}>
            {day}
          </Text>
        </TouchableOpacity>
      );
    }
    return days;
  }, [scheduleDate, scheduleHour, scheduleMinute]);

  // ─── Step Indicator ───────────────────────────────────────────────────────
  const renderStepIndicator = () => {
    if (currentStep === 4) return null;
    const steps = [
      { n: 1, label: 'Recipients' },
      { n: 2, label: 'Message' },
      { n: 3, label: 'Review' },
    ];
    return (
      <View style={styles.stepIndicatorRow}>
        {steps.map((step, idx) => {
          const done = currentStep > step.n;
          const active = currentStep === step.n;
          return (
            <React.Fragment key={step.n}>
              <TouchableOpacity
                style={styles.stepItemWrapper}
                onPress={() => { if (done) setCurrentStep(step.n as WizardStep); }}
                disabled={!done}
                activeOpacity={0.7}
              >
                <View style={[styles.stepCircle, active && styles.stepCircleActive, done && styles.stepCircleDone]}>
                  {done ? (
                    <Ionicons name="checkmark" size={14} color="#fff" />
                  ) : (
                    <Text style={[styles.stepCircleText, active && styles.stepCircleTextActive]}>{step.n}</Text>
                  )}
                </View>
                <Text style={[styles.stepLabel, active && styles.stepLabelActive, done && styles.stepLabelDone]}>
                  {step.label}
                </Text>
              </TouchableOpacity>
              {idx < steps.length - 1 && (
                <View style={[styles.stepLine, done && styles.stepLineActive]} />
              )}
            </React.Fragment>
          );
        })}
      </View>
    );
  };

  // ─── Step 1: Recipients ───────────────────────────────────────────────────
  const renderStep1 = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepTitle}>Who will receive this message?</Text>
      <Text style={styles.stepSubtitle}>
        Add recipients manually, pick from contacts, or upload a CSV file.
      </Text>

      <RecipientChipsInput
        recipients={recipients}
        onAdd={handleAddRecipient}
        onRemove={handleRemoveRecipient}
        onImportFromDevice={handleImportFromDevice}
        onImportFromFile={handleImportFromFile}
        onDownloadTemplate={handleDownloadTemplate}
        isDisabled={isLoading || isSending || isLimitReached}
      />

      {isImporting && (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={styles.loadingText}>Importing contacts…</Text>
        </View>
      )}

      {isLimitReached && (
        <View style={styles.limitBanner}>
          <Ionicons name="warning" size={16} color={colors.error} />
          <Text style={styles.limitBannerText}>{messageLimits?.message || 'Message limit reached.'}</Text>
        </View>
      )}

      <View style={styles.stepNav}>
        <View style={styles.stepNavLeft}>
          {recipientCount > 0 && (
            <Text style={styles.recipientCountBadge}>
              {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} added
            </Text>
          )}
        </View>
        <Button
          title="Next →"
          onPress={() => setCurrentStep(2)}
          variant="primary"
          disabled={!canProceedToStep2 || isLimitReached}
        />
      </View>
    </View>
  );

  // ─── Step 2: Message / Template ───────────────────────────────────────────
  const renderStep2 = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepTitle}>Compose your message</Text>

      {isLoadingTemplates ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={styles.loadingText}>Loading templates…</Text>
        </View>
      ) : selectedTemplate ? (
        <View style={styles.selectedTemplateCard}>
          <View style={styles.selectedTemplateCardHeader}>
            <View style={styles.selectedTemplateCardInfo}>
              {selectedTemplate.isDefault && (
                <View style={styles.defaultBadge}>
                  <Ionicons name="star" size={11} color={colors.accent} />
                  <Text style={styles.defaultBadgeText}>Default</Text>
                </View>
              )}
              <Text style={styles.selectedTemplateName}>{selectedTemplate.templateName}</Text>
              <Text style={styles.selectedTemplateCategory}>{selectedTemplate.category}</Text>
            </View>
            <TouchableOpacity
              style={styles.changeTemplateBtn}
              onPress={() => setShowTemplateSelector(true)}
            >
              <Text style={styles.changeTemplateBtnText}>Change</Text>
              <Ionicons name="chevron-down" size={14} color={colors.primary} />
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <TouchableOpacity style={styles.selectTemplateBtn} onPress={() => setShowTemplateSelector(true)}>
          <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
          <Text style={styles.selectTemplateBtnText}>Choose Template</Text>
        </TouchableOpacity>
      )}

      {selectedTemplate && (
        <WhatsAppMessagePreviewEditable
          template={selectedTemplate}
          templateParameters={templateParameters}
          onParameterChange={(pos, val) =>
            setTemplateParameters(prev => ({ ...prev, [pos]: val }))
          }
          headerAsset={headerAsset}
          onAssetChange={setHeaderAsset}
          missingPositions={
            derivedTemplateParameters
              .filter((p: any) => p.isRequired && !templateParameters[p.position]?.trim())
              .map((p: any) => p.position)
          }
          campaignName={taskName || 'campaign'}
          businessId={currentBusiness?._id}
          businessName={currentBusiness?.name}
        />
      )}

      <View style={styles.stepNav}>
        <TouchableOpacity style={styles.backBtn} onPress={() => setCurrentStep(1)}>
          <DirectionalIcon name="arrow-back" size={18} color={colors.text.secondary} />
          <Text style={styles.backBtnText}>Back</Text>
        </TouchableOpacity>
        <Button
          title={`Preview →`}
          onPress={() => setCurrentStep(3)}
          variant="primary"
          disabled={!canProceedToStep3}
        />
      </View>
    </View>
  );

  // ─── Step 3: Review & Send ────────────────────────────────────────────────
  const renderStep3 = () => {
    const isOverQuota = messageLimits ? !messageLimits.canSend : false;

    return (
      <View style={styles.stepContent}>
        <Text style={styles.stepTitle}>Review & Send</Text>

        {/* Final message preview */}
        <Text style={styles.reviewSectionLabel}>Message preview</Text>
        <WhatsAppMessagePreview
          template={messageSource === 'custom-template' ? selectedTemplate : undefined}
          customMessage={messageSource === 'custom-message' ? message : undefined}
          templateParameters={templateParameters}
          headerAsset={headerAsset}
          businessName={currentBusiness?.name}
        />

        {/* Recipients summary */}
        <Text style={styles.reviewSectionLabel}>Recipients ({recipientCount})</Text>
        <View style={styles.recipientsSummary}>
          {recipients.slice(0, 5).map(chip => (
            <View key={chip.id} style={styles.recipientSummaryRow}>
              <Ionicons
                name={chip.isExisting ? 'person' : 'call-outline'}
                size={14}
                color={chip.isExisting ? colors.success : colors.text.secondary}
              />
              <Text style={styles.recipientSummaryText}>{chip.displayName}</Text>
            </View>
          ))}
          {recipients.length > 5 && (
            <Text style={styles.recipientsMore}>+{recipients.length - 5} more</Text>
          )}
        </View>

        {/* Task name */}
        <Text style={styles.reviewSectionLabel}>Task name (optional)</Text>
        <Input
          value={taskName}
          onChangeText={setTaskName}
          placeholder={defaultTaskName}
          editable={!isLoading && !isSending}
        />

        {/* Schedule toggle */}
        <View style={styles.scheduleSection}>
          <TouchableOpacity
            style={styles.scheduleToggleRow}
            onPress={() => setIsScheduleEnabled(v => !v)}
            disabled={isLoading || isSending}
          >
            <View style={styles.scheduleToggleLeft}>
              <Ionicons name="time-outline" size={18} color={colors.primary} />
              <Text style={styles.scheduleToggleLabel}>Schedule for later</Text>
            </View>
            <View style={[styles.toggleSwitch, isScheduleEnabled && styles.toggleSwitchActive]}>
              <View style={[styles.toggleThumb, isScheduleEnabled && styles.toggleThumbActive]} />
            </View>
          </TouchableOpacity>

          {isScheduleEnabled && (
            <View style={styles.schedulePickers}>
              {Platform.OS === 'web' ? (
                <View style={styles.webDateTimeRow}>
                  {React.createElement('input', {
                    type: 'date',
                    value: `${scheduleDate.getFullYear()}-${String(scheduleDate.getMonth() + 1).padStart(2, '0')}-${String(scheduleDate.getDate()).padStart(2, '0')}`,
                    onChange: (e: any) => {
                      const [y, mo, day] = e.target.value.split('-').map(Number);
                      const d = new Date(y, mo - 1, day, scheduleHour, scheduleMinute);
                      setScheduleDate(d);
                    },
                    min: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`,
                    style: { flex: 1, padding: 10, fontSize: 15, border: `1px solid ${colors.borderMedium}`, borderRadius: 8, minHeight: 44 },
                    disabled: isLoading || isSending,
                  } as any)}
                  {React.createElement('input', {
                    type: 'time',
                    key: `${scheduleHour}:${scheduleMinute}`,
                    defaultValue: `${String(scheduleHour).padStart(2, '0')}:${String(scheduleMinute).padStart(2, '0')}`,
                    onBlur: (e: any) => {
                      const [h, m] = e.target.value.split(':').map(Number);
                      if (!isNaN(h) && !isNaN(m)) {
                        setScheduleHour(h);
                        setScheduleMinute(m);
                      }
                    },
                    style: { flex: 1, padding: 10, fontSize: 15, border: `1px solid ${colors.borderMedium}`, borderRadius: 8, minHeight: 44 },
                    disabled: isLoading || isSending,
                  } as any)}
                </View>
              ) : (
                <View style={styles.nativeDateTimeRow}>
                  <TouchableOpacity style={styles.schedulePickerBtn} onPress={() => setShowDatePicker(true)}>
                    <Ionicons name="calendar-outline" size={18} color={colors.primary} />
                    <Text style={styles.schedulePickerBtnText}>
                      {scheduleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.schedulePickerBtn} onPress={() => setShowTimePicker(true)}>
                    <Ionicons name="time-outline" size={18} color={colors.primary} />
                    <Text style={styles.schedulePickerBtnText}>
                      {`${String(get12HourFormat(scheduleHour)).padStart(2, '0')}:${String(scheduleMinute).padStart(2, '0')} ${scheduleHour < 12 ? 'AM' : 'PM'}`}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Send buttons */}
        <View style={styles.stepNav}>
          <TouchableOpacity style={styles.backBtn} onPress={() => setCurrentStep(2)}>
            <DirectionalIcon name="arrow-back" size={18} color={colors.text.secondary} />
            <Text style={styles.backBtnText}>Back</Text>
          </TouchableOpacity>
          <Button
            title={isLoading || isSending ? 'Sending…' : isScheduleEnabled ? 'Schedule' : 'Send Now'}
            onPress={handleSend}
            variant="primary"
            disabled={isLoading || isSending || isOverQuota}
            loading={isLoading || isSending}
          />
        </View>
      </View>
    );
  };

  // ─── Step 4: Done ─────────────────────────────────────────────────────────
  const renderStep4 = () => (
    <View style={styles.doneContainer}>
      <View style={styles.doneIconWrapper}>
        <Ionicons name="checkmark-circle" size={72} color={colors.success} />
      </View>
      <Text style={styles.doneTitle}>Task queued successfully!</Text>
      <Text style={styles.doneSubtitle}>
        Your message is being sent to {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} in the background.
        {'\n'}You'll receive a notification when it's complete.
      </Text>
      <View style={styles.doneBtns}>
        <Button
          title="View Conversations"
          onPress={() => navigateTo('chat')}
          variant="outline"
          fullWidth
        />
        <Button
          title="Send Another Message"
          onPress={resetForm}
          variant="primary"
          fullWidth
        />
      </View>
    </View>
  );

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={!isMobile}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async itemId => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={!isMobile}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
    >
      {/* ── Device contact picker modal ─────────────────────────────────── */}
      {Platform.OS !== 'web' && (
        <Modal visible={showContactPicker} animationType="slide" transparent onRequestClose={() => setShowContactPicker(false)}>
          <View style={styles.pickerOverlay}>
            <View style={styles.pickerContent}>
              <View style={styles.pickerHeader}>
                <Text style={styles.pickerTitle}>Select contacts</Text>
                <TouchableOpacity onPress={() => setShowContactPicker(false)} hitSlop={12}>
                  <Ionicons name="close" size={24} color={colors.text.primary} />
                </TouchableOpacity>
              </View>
              <TextInput
                style={styles.pickerSearch}
                placeholder="Search by name or number…"
                placeholderTextColor={colors.text.secondary}
                value={contactSearchQuery}
                onChangeText={setContactSearchQuery}
              />
              <View style={styles.pickerSelectAllRow}>
                <TouchableOpacity onPress={selectAllContacts}>
                  <Text style={styles.pickerSelectAllText}>
                    {selectedContactIds.size === filteredDeviceContacts.length && filteredDeviceContacts.length > 0
                      ? 'Deselect all'
                      : 'Select all'}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.pickerCountText}>{selectedContactIds.size} selected</Text>
              </View>
              {contactPickerLoading ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator size="large" color={colors.primary} />
                  <Text style={styles.loadingText}>Loading contacts…</Text>
                </View>
              ) : (
                <FlatList
                  data={filteredDeviceContacts}
                  keyExtractor={(item, index) => item.id ?? `contact-${index}`}
                  style={styles.pickerList}
                  ListEmptyComponent={
                    <Text style={styles.pickerEmpty}>
                      {contactSearchQuery.trim() ? 'No contacts match your search' : 'No contacts with phone numbers'}
                    </Text>
                  }
                  renderItem={({ item, index }) => {
                    const phone = item.phoneNumbers?.[0]?.number || '';
                    const key = item.id ?? `contact-${index}`;
                    const isSelected = selectedContactIds.has(key);
                    return (
                      <TouchableOpacity
                        style={[styles.pickerRow, isSelected && styles.pickerRowSelected]}
                        onPress={() => toggleContactSelection(item.id, index)}
                        activeOpacity={0.7}
                      >
                        <View style={[styles.pickerCheckbox, isSelected && styles.pickerCheckboxSelected]}>
                          {isSelected && <Ionicons name="checkmark" size={16} color="#fff" />}
                        </View>
                        <View>
                          <Text style={styles.pickerRowName} numberOfLines={1}>{item.name || 'No name'}</Text>
                          <Text style={styles.pickerRowPhone} numberOfLines={1}>{phone}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  }}
                />
              )}
              <View style={styles.pickerFooter}>
                <Button
                  title={`Add ${selectedContactIds.size} contact${selectedContactIds.size !== 1 ? 's' : ''}`}
                  onPress={addSelectedContactsToForm}
                  variant="primary"
                  disabled={selectedContactIds.size === 0}
                />
              </View>
            </View>
          </View>
        </Modal>
      )}

      {/* ── Date picker modal ────────────────────────────────────────────── */}
      {showDatePicker && (
        <Modal visible={showDatePicker} animationType="slide" transparent onRequestClose={() => setShowDatePicker(false)}>
          <View style={styles.pickerOverlay}>
            <View style={[styles.pickerContent, styles.calendarContent]}>
              <View style={[styles.pickerHeader, { flexDirection: rtl.rowDirection }]}>
                <TouchableOpacity
                  onPress={() => setScheduleDate(new Date(scheduleDate.getFullYear(), scheduleDate.getMonth() - 1, 1))}
                >
                  <DirectionalIcon name="chevron-back" size={22} color={colors.primary} />
                </TouchableOpacity>
                <Text style={styles.calendarMonthTitle}>
                  {scheduleDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Text>
                <TouchableOpacity
                  onPress={() => setScheduleDate(new Date(scheduleDate.getFullYear(), scheduleDate.getMonth() + 1, 1))}
                >
                  <DirectionalIcon name="chevron-forward" size={22} color={colors.primary} />
                </TouchableOpacity>
              </View>
              <View style={styles.calendarGrid}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                  <Text key={d} style={styles.calendarDayHeader}>{d}</Text>
                ))}
                {renderCalendarDays()}
              </View>
              <Button title="Done" onPress={() => setShowDatePicker(false)} variant="primary" style={styles.pickerDoneBtn} />
            </View>
          </View>
        </Modal>
      )}

      {/* ── Time picker modal ────────────────────────────────────────────── */}
      {showTimePicker && (
        <Modal visible={showTimePicker} animationType="slide" transparent onRequestClose={() => setShowTimePicker(false)}>
          <View style={styles.pickerOverlay}>
            <View style={[styles.pickerContent, styles.timeContent]}>
              <Text style={styles.pickerTitle}>Select Time</Text>
              <View style={styles.timeRow}>
                <View style={styles.timeSpinner}>
                  <Text style={styles.timeSpinnerLabel}>Hour</Text>
                  <TouchableOpacity onPress={() => setScheduleHour(convertTo24Hour(Math.min(12, get12HourFormat(scheduleHour) + 1), isAM))}>
                    <Ionicons name="chevron-down" size={20} color={colors.primary} />
                  </TouchableOpacity>
                  <Text style={styles.timeSpinnerValue}>{String(get12HourFormat(scheduleHour)).padStart(2, '0')}</Text>
                  <TouchableOpacity onPress={() => setScheduleHour(convertTo24Hour(Math.max(1, get12HourFormat(scheduleHour) - 1), isAM))}>
                    <Ionicons name="chevron-up" size={20} color={colors.primary} />
                  </TouchableOpacity>
                </View>
                <Text style={styles.timeColon}>:</Text>
                <View style={styles.timeSpinner}>
                  <Text style={styles.timeSpinnerLabel}>Minute</Text>
                  <TouchableOpacity onPress={() => setScheduleMinute(Math.min(59, scheduleMinute + 5))}>
                    <Ionicons name="chevron-down" size={20} color={colors.primary} />
                  </TouchableOpacity>
                  <Text style={styles.timeSpinnerValue}>{String(scheduleMinute).padStart(2, '0')}</Text>
                  <TouchableOpacity onPress={() => setScheduleMinute(Math.max(0, scheduleMinute - 5))}>
                    <Ionicons name="chevron-up" size={20} color={colors.primary} />
                  </TouchableOpacity>
                </View>
                <View style={styles.timeSpinner}>
                  <Text style={styles.timeSpinnerLabel}>AM/PM</Text>
                  <TouchableOpacity
                    style={styles.ampmBtn}
                    onPress={() => {
                      const newIsAM = !isAM;
                      setIsAM(newIsAM);
                      setScheduleHour(convertTo24Hour(get12HourFormat(scheduleHour), newIsAM));
                    }}
                  >
                    <Text style={styles.ampmBtnText}>{isAM ? 'AM' : 'PM'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
              <Button title="Done" onPress={() => setShowTimePicker(false)} variant="primary" style={styles.pickerDoneBtn} />
            </View>
          </View>
        </Modal>
      )}

      {/* ── Template bottom sheet ────────────────────────────────────────── */}
      {showTemplateSelector && (
        <Modal visible={showTemplateSelector} animationType="slide" transparent onRequestClose={() => setShowTemplateSelector(false)}>
          <View style={styles.bottomSheetOverlay}>
            <TouchableOpacity style={styles.bottomSheetBackdrop} activeOpacity={1} onPress={() => setShowTemplateSelector(false)} />
            <View style={styles.bottomSheetContent}>
              <View style={styles.bottomSheetHandle} />
              <TemplateList
                templates={templates}
                isLoading={isLoadingTemplates}
                hasTemplates={hasTemplates}
                onSelectTemplate={async template => {
                  setSelectedTemplate(template);
                  setTemplateParameters({});
                  setHeaderAsset(template.headerAsset || undefined);
                  setParameterErrors([]);
                  setMessageSource('custom-template');
                  setShowTemplateSelector(false);
                  // Fetch full detail if parameters are missing, OR if the list didn't include headerAsset
                  // (headerAsset is now returned by the list API, but fetch detail as safety net for older cached data)
                  const needsDetail =
                    (template.hasParameters && (!template.parameters || template.parameters.length === 0));
                  if (needsDetail) {
                    try {
                      const detail = await apiService.getWhatsAppCustomTemplate(template._id);
                      if (detail?.data?._id) {
                        setSelectedTemplate(detail.data);
                        setHeaderAsset(detail.data.headerAsset || undefined);
                      }
                    } catch {}
                  }
                }}
                onClose={() => setShowTemplateSelector(false)}
              />
            </View>
          </View>
        </Modal>
      )}

      {/* ── Main wizard content ──────────────────────────────────────────── */}
      <KeyboardAvoidingView
        style={styles.flex1}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        <ScrollView
          style={styles.flex1}
          contentContainerStyle={[styles.scrollContent, !isMobile && styles.scrollContentDesktop]}
          showsVerticalScrollIndicator={isMobile}
          keyboardShouldPersistTaps="handled"
        >
          {/* Screen title */}
          <View style={styles.screenHeader}>
            <View style={styles.screenTitleRow}>
              <View style={styles.whatsappIconBadge}>
                <Ionicons name="logo-whatsapp" size={20} color="#fff" />
              </View>
              <Text style={styles.screenTitle}>WhatsApp Messaging</Text>
            </View>
          </View>

          {/* Step indicator */}
          {renderStepIndicator()}

          {/* Step content */}
          <View style={[styles.card, !isMobile && styles.cardDesktop]}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  flex1: { flex: 1 },

  // ── Screen layout ─────────────────────────────────────────────────────────
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    paddingBottom: 100,
  },
  scrollContentDesktop: {
    paddingHorizontal: spacing.lg,
  },
  screenHeader: {
    marginBottom: spacing.md,
  },
  screenTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  whatsappIconBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#25D366',
    justifyContent: 'center',
    alignItems: 'center',
  },
  screenTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.text.primary,
  },

  // ── Step indicator ────────────────────────────────────────────────────────
  stepIndicatorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
    paddingHorizontal: spacing.sm,
  },
  stepItemWrapper: {
    alignItems: 'center',
    gap: 6,
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: colors.borderMedium,
    backgroundColor: colors.background.secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepCircleActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  stepCircleDone: {
    borderColor: colors.success,
    backgroundColor: colors.success,
  },
  stepCircleText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text.secondary,
  },
  stepCircleTextActive: {
    color: '#fff',
  },
  stepLabel: {
    fontSize: 11,
    color: colors.text.secondary,
    fontWeight: '400',
  },
  stepLabelActive: {
    color: colors.primary,
    fontWeight: '600',
  },
  stepLabelDone: {
    color: colors.success,
  },
  stepLine: {
    flex: 1,
    height: 2,
    backgroundColor: colors.borderMedium,
    marginHorizontal: 6,
    marginBottom: 18,
  },
  stepLineActive: {
    backgroundColor: colors.success,
  },

  // ── Card ──────────────────────────────────────────────────────────────────
  card: {
    backgroundColor: colors.background.primary,
    borderRadius: spacing.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.borderLight,
    padding: spacing.md,
  },
  cardDesktop: {
    padding: spacing.lg,
  },

  // ── Step content common ───────────────────────────────────────────────────
  stepContent: {
    gap: spacing.md,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: 2,
  },
  stepSubtitle: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
  },

  // ── Step navigation ───────────────────────────────────────────────────────
  stepNav: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    marginTop: spacing.sm,
    gap: spacing.sm,
  },
  stepNavLeft: {
    flex: 1,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.borderMedium,
  },
  backBtnText: {
    fontSize: 14,
    color: colors.text.secondary,
  },

  // ── Step 1 ────────────────────────────────────────────────────────────────
  recipientCountBadge: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '600',
  },
  limitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(220, 53, 69, 0.08)',
    borderRadius: spacing.borderRadius.sm,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.error,
  },
  limitBannerText: {
    fontSize: 13,
    color: colors.error,
    flex: 1,
  },

  // ── Step 2 ────────────────────────────────────────────────────────────────
  segmentedControl: {
    flexDirection: 'row',
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    overflow: 'hidden',
  },
  segmentBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    backgroundColor: colors.background.secondary,
  },
  segmentBtnActive: {
    backgroundColor: colors.primaryTransparent,
  },
  segmentBtnText: {
    fontSize: 13,
    color: colors.text.secondary,
    fontWeight: '500',
  },
  segmentBtnTextActive: {
    color: colors.primary,
    fontWeight: '600',
  },
  warningText: {
    fontSize: 12,
    color: colors.warning,
  },
  selectedTemplateCard: {
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    backgroundColor: colors.primaryTransparent,
  },
  selectedTemplateCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  selectedTemplateCardInfo: {
    flex: 1,
    gap: 4,
  },
  defaultBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(247, 180, 44, 0.12)',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  defaultBadgeText: {
    fontSize: 11,
    color: '#856404',
    fontWeight: '600',
  },
  selectedTemplateName: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text.primary,
  },
  selectedTemplateCategory: {
    fontSize: 12,
    color: colors.text.secondary,
  },
  changeTemplateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.background.surface,
  },
  changeTemplateBtnText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '600',
  },
  selectTemplateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    borderStyle: 'dashed',
  },
  selectTemplateBtnText: {
    fontSize: 15,
    color: colors.primary,
    fontWeight: '600',
  },
  paramRow: {
    gap: 6,
  },
  previewSection: {
    gap: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    paddingTop: spacing.md,
  },
  previewSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // ── Step 3 ────────────────────────────────────────────────────────────────
  quotaCard: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    gap: spacing.sm,
  },
  quotaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quotaLabel: {
    fontSize: 14,
    color: colors.text.primary,
  },
  quotaBold: {
    fontWeight: '700',
  },
  quotaFraction: {
    fontSize: 13,
    color: colors.text.secondary,
    fontWeight: '600',
  },
  quotaFractionOver: {
    color: colors.error,
  },
  quotaBarTrack: {
    height: 6,
    backgroundColor: colors.borderMedium,
    borderRadius: 3,
    overflow: 'hidden',
  },
  quotaBarFill: {
    height: 6,
    backgroundColor: colors.primary,
    borderRadius: 3,
  },
  quotaBarFillOver: {
    backgroundColor: colors.error,
  },
  quotaOverText: {
    fontSize: 12,
    color: colors.error,
  },
  reviewSectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: spacing.sm,
  },
  recipientsSummary: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.sm,
    gap: 6,
  },
  recipientSummaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  recipientSummaryText: {
    fontSize: 14,
    color: colors.text.primary,
  },
  recipientsMore: {
    fontSize: 13,
    color: colors.text.secondary,
    fontStyle: 'italic',
    paddingTop: 2,
  },

  // ── Schedule ──────────────────────────────────────────────────────────────
  scheduleSection: {
    gap: spacing.sm,
  },
  scheduleToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
  },
  scheduleToggleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  scheduleToggleLabel: {
    fontSize: 15,
    color: colors.text.primary,
    fontWeight: '500',
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.borderMedium,
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  toggleSwitchActive: {
    backgroundColor: colors.primary,
  },
  toggleThumb: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#fff',
    alignSelf: 'flex-start',
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  schedulePickers: {
    gap: spacing.sm,
  },
  webDateTimeRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  nativeDateTimeRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  schedulePickerBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.primary,
  },
  schedulePickerBtnText: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '500',
  },

  // ── Step 4: Done ──────────────────────────────────────────────────────────
  doneContainer: {
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.xl,
  },
  doneIconWrapper: {
    marginBottom: spacing.sm,
  },
  doneTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.text.primary,
    textAlign: 'center',
  },
  doneSubtitle: {
    fontSize: 14,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 300,
  },
  doneBtns: {
    width: '100%',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },

  // ── Shared loading/misc ───────────────────────────────────────────────────
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
  },
  loadingText: {
    fontSize: 14,
    color: colors.text.secondary,
  },

  // ── Bottom sheet ──────────────────────────────────────────────────────────
  bottomSheetOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  bottomSheetBackdrop: {
    flex: 1,
  },
  bottomSheetContent: {
    backgroundColor: colors.background.primary,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '72%',
  },
  bottomSheetHandle: {
    width: 40,
    height: 4,
    backgroundColor: colors.borderMedium,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 4,
  },

  // ── Device contact picker ─────────────────────────────────────────────────
  pickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  pickerContent: {
    backgroundColor: colors.background.primary,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%',
    paddingTop: spacing.md,
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  pickerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.text.primary,
  },
  pickerSearch: {
    margin: spacing.sm,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
    fontSize: 14,
    color: colors.text.primary,
    backgroundColor: colors.background.secondary,
  },
  pickerSelectAllRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
  },
  pickerSelectAllText: {
    fontSize: 13,
    color: colors.primary,
    fontWeight: '600',
  },
  pickerCountText: {
    fontSize: 13,
    color: colors.text.secondary,
  },
  pickerList: {
    flex: 1,
  },
  pickerEmpty: {
    padding: spacing.md,
    textAlign: 'center',
    color: colors.text.secondary,
    fontSize: 14,
  },
  pickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  pickerRowSelected: {
    backgroundColor: colors.primaryTransparent,
  },
  pickerCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.borderMedium,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pickerCheckboxSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  pickerRowName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text.primary,
  },
  pickerRowPhone: {
    fontSize: 13,
    color: colors.text.secondary,
  },
  pickerFooter: {
    padding: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },

  // ── Calendar ──────────────────────────────────────────────────────────────
  calendarContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },
  calendarMonthTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text.primary,
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: spacing.sm,
  },
  calendarDayHeader: {
    width: `${100 / 7}%` as any,
    textAlign: 'center',
    fontSize: 12,
    color: colors.text.secondary,
    fontWeight: '600',
    paddingVertical: 6,
  },
  calendarDayCell: {
    width: `${100 / 7}%` as any,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  calendarDaySelected: {
    backgroundColor: colors.primary,
    borderRadius: 20,
  },
  calendarDayPast: {
    opacity: 0.3,
  },
  calendarDayText: {
    fontSize: 14,
    color: colors.text.primary,
  },
  calendarDayTextSelected: {
    color: '#fff',
    fontWeight: '700',
  },
  calendarDayTextPast: {
    color: colors.text.secondary,
  },

  // ── Time picker ───────────────────────────────────────────────────────────
  timeContent: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  timeSpinner: {
    alignItems: 'center',
    gap: 8,
  },
  timeSpinnerLabel: {
    fontSize: 12,
    color: colors.text.secondary,
    fontWeight: '600',
  },
  timeSpinnerValue: {
    fontSize: 32,
    fontWeight: '700',
    color: colors.text.primary,
    minWidth: 52,
    textAlign: 'center',
  },
  timeColon: {
    fontSize: 32,
    fontWeight: '700',
    color: colors.text.secondary,
    marginTop: 24,
  },
  ampmBtn: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginTop: 8,
  },
  ampmBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#fff',
  },
  pickerDoneBtn: {
    marginTop: spacing.md,
  },
});
